<script>
import { cloneDeep } from "lodash";
import { mapGetters } from "vuex";
import workflowService from "@/api/ativacaoped/workflow.service";
import api from "@/api/api";
import Modal from "@/components/g-modal.vue";

export default {
  components: { Modal },

  data() {
    return {
      parameters: [],
      loading: false,
      modalOpened: false,
      editingParameter: null,
      dragIndex: null,
      dragOverIndex: null,
      niveisHierarquicosOptions: []
    };
  },

  computed: {
    ...mapGetters(["clientId"]),

    modalTitle() {
      return this.editingParameter?.id ? "Editar Etapa" : "Nova Etapa";
    },

    headers() {
      return [
        { text: "", value: "drag", sortable: false, width: 40, minWidth: 40 },
        { text: "Ordem", value: "ordem", width: 80, minWidth: 80 },
        { text: "Nome", value: "nome", minWidth: 200 },
        { text: "SLA", value: "sla", minWidth: 80 },
        { text: "Def. Revisores", value: "definir_revisores", minWidth: 120 },
        { text: "Min. aprovações", value: "quantidade_revisores_aprovacao", minWidth: 130 },
        { text: "Revisores", value: "revisores", minWidth: 150 },
        { text: "", value: "acoes", sortable: false, width: 80, minWidth: 80 }
      ];
    }
  },

  async created() {
    await Promise.all([this.doLoad(), this.loadNiveisHierarquicos()]);
  },

  methods: {
    async doLoad() {
      this.loading = true;

      try {
        const response = await workflowService.getWorkflowParametrizacao(
          this.clientId
        );

        this.parameters = Array.isArray(response)
          ? response.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
          : [];
      } catch (error) {
        this.notify({
          title: "Erro!",
          text:
            error?.response?.data?.message ||
            error?.message ||
            "Erro ao carregar parâmetros.",
          type: "error",
          group: "geral",
          duration: 5000
        });

        if (!error.isAxiosError) {
          this.notify(error);
        }

        this.parameters = [];
      } finally {
        this.loading = false;
      }
    },

    openCreateModal() {
      this.editingParameter = null;

      this.$nextTick(() => {
        this.editingParameter = {
          nome: "",
          sla: null,
          quantidade_revisores_aprovacao: null,
          definir_revisores: false,
          revisores: null,
          revisores_tipo_id: null
        };

        this.modalOpened = true;
      });
    },

    openEditModal(parameter) {
      this.editingParameter = cloneDeep(parameter);
      this.modalOpened = true;
    },

    closeModal() {
      this.modalOpened = false;
      this.editingParameter = null;
    },

    async handleSave(value) {
      try {
        const payload = {
          ...value,
          ordem: value.ordem ?? this.parameters.length + 1
        };

        if (value.id) {
          await workflowService.updateWorkflowParametrizacao(
            this.clientId,
            payload
          );
          this.$notify({
            title: "Sucesso!",
            text: "Etapa atualizada.",
            type: "success",
            group: "geral",
            duration: 5000
          });
        } else {
          await workflowService.createWorkflowParametrizacao(
            this.clientId,
            payload
          );
          this.$notify({
            title: "Sucesso!",
            text: "Etapa criada.",
            type: "success",
            group: "geral",
            duration: 5000
          });
        }

        await this.doLoad();
        this.closeModal();
      } catch (error) {
        this.notify(error);
      }
    },

    handleDelete(parameter) {
      if (!parameter.id) return;

      this.notify({
        title: "Aviso!",
        text: "Exclusão pendente.",
        type: "warning",
        group: "geral",
        duration: 5000
      });
    },

    async loadNiveisHierarquicos() {
      try {
        const response = await api.get(
          `v1/ativacaoped/${this.clientId}/nivel-hierarquicos/tipos`
        );
        this.niveisHierarquicosOptions = response.data?.data ?? response.data ?? [];
      } catch (error) {
        this.notify(error);
        this.niveisHierarquicosOptions = [];
      }
    },

        onDragStart(e, index) {
      this.dragIndex = index;
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
    },

    onDragOver(e, index) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      this.dragOverIndex = index;
    },

    onDrop(e, index) {
      e.preventDefault();
      this.dragOverIndex = null;
      if (this.dragIndex === null || this.dragIndex === index) return;
      const items = [...this.parameters];
      const [moved] = items.splice(this.dragIndex, 1);
      items.splice(index, 0, moved);
      items.forEach((p, i) => { p.ordem = i + 1; });
      this.parameters = items;
      this.dragIndex = null;
    },

    onDragEnd() { this.dragIndex = null; this.dragOverIndex = null; }
  }
};
</script>

<template>
  <v-card
    :color="tableColor"
    elevation="0"
    class="d-flex flex-column"
    style="height: 100%"
  >
    <v-card-text class="d-flex align-center" style="min-height: 56px">
      <div class="table-v-action-button mr-2" @click="doLoad">
        <v-icon>mdi-refresh</v-icon>
        Atualizar
      </div>

      <div class="table-v-action-button mr-2" @click="openCreateModal">
        <v-icon>mdi-plus-box-outline</v-icon>
        Adicionar
      </div>
    </v-card-text>

    <v-card-text
      class="pa-0 flex-grow-1 d-flex flex-column"
      style="overflow-y: auto; min-height: 0"
    >
      <v-data-table
        :headers="headers"
        :items="parameters"
        :loading="loading"
        hide-default-footer
        disable-pagination
        disable-sort
        no-data-text="Nenhum parâmetro encontrado."
        class="elevation-0 fill-height"
      >
        <template v-slot:body="{ items }">
          <tbody>
            <tr v-for="(item, index) in items" :key="item.id"
              draggable="true"
              @dragstart="onDragStart($event, index)"
              @dragover="onDragOver($event, index)"
              @drop="onDrop($event, index)"
              @dragend="onDragEnd"
              :class="{ 'drag-over-row': dragOverIndex === index }"
            >
              <td class="text-center px-0"><v-icon class="drag-handle">mdi-drag</v-icon></td>
              <td>{{ item.ordem }}</td>
              <td>{{ item.nome }}</td>
              <td>{{ item.sla }}d</td>
              <td><v-icon small :color="item.definir_revisores ? 'green' : 'grey'">{{ item.definir_revisores ? "mdi-check" : "mdi-close" }}</v-icon></td>
              <td>{{ item.quantidade_revisores_aprovacao }}</td>
              <td><span v-if="item.revisores && item.revisores.length" :title="item.revisores.map(r=>r.nome).join(', ')">{{ item.revisores.map(r=>r.nome).join(", ") }}</span><span v-else class="grey--text">--</span></td>
              <td><v-icon small class="action-icon" @click="openEditModal(item)" title="Editar">mdi-pencil</v-icon></td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card-text>

    <Modal
      :opened.sync="modalOpened"
      :title="modalTitle"
      appendTitleIcon="mdi-square-edit-outline"
      width="600"
    >
      <v-container v-if="editingParameter" class="pa-0 pb-3">
        <v-row dense>
          <v-col cols="12">
            <v-text-field
              v-model="editingParameter.nome"
              label="Nome"
              dense
              outlined
              :rules="[v => !!v || 'Obrigatório.']"
              required
            />
          </v-col>

          <v-col cols="6" class="pb-0">
            <v-text-field
              v-model="editingParameter.sla"
              label="SLA (dias úteis)"
              dense
              outlined
              type="number"
              :rules="[v => (v != null && v >= 0) || 'Obrigatório.']"
            />
          </v-col>

          <v-col cols="6" class="pb-0">
            <v-text-field
              v-model="editingParameter.quantidade_revisores_aprovacao"
              label="Min. revisores"
              dense
              outlined
              type="number"
              :rules="[v => (v != null && v >= 1) || 'Obrigatório.']"
            />
          </v-col>

          <v-col cols="6" class="pb-0">
            <v-autocomplete
              v-model="editingParameter.revisores_tipo_id"
              :items="niveisHierarquicosOptions"
              item-text="nome"
              item-value="id"
              label="Tipo de Revisor"
              dense
              outlined
              multiple
              clearable
              hide-details
              :rules="
                editingParameter.definir_revisores
                  ? [v => (v && v.length > 0) || 'Obrigatório.']
                  : []
              "
            />
          </v-col>

          <v-col cols="6" class="pb-0">
            <v-switch
              v-model="editingParameter.definir_revisores"
              dense
              :label="
                editingParameter.definir_revisores
                  ? 'Revisores são escolhidos'
                  : 'Revisores escolhem'
              "
              hide-details
            />
          </v-col>
        </v-row>
      </v-container>

      <template #buttons="{ close }">
        <v-spacer />

        <v-btn class="px-5 ml-3" color="secondary" text @click="close(null)">
          Cancelar
        </v-btn>

        <v-btn
          class="px-5 ml-3"
          color="primary"
          dark
          depressed
          @click="handleSave(editingParameter)"
        >
          Salvar
        </v-btn>
      </template>
    </Modal>
  </v-card>
</template>

<style lang="scss" scoped>
.table-v-action-button { user-select: none; }
.drag-handle { cursor: grab; }
.drag-handle:active { cursor: grabbing; }
.action-icon { cursor: pointer; }

::v-deep .v-data-table.fill-height {
  height: 100%;
  > .v-data-table__wrapper { height: 100%; overflow-y: auto; }
}

::v-deep tr.drag-over-row td {
  border-top: 2px solid #1c1c44 !important;
}
</style>
